import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { defaultIfEmpty, filter, first, map, Observable, withLatestFrom } from 'rxjs';

import { getCmsConfigValue } from '@core/infrastructure/state/core.selectors';
import { CategoriesSelectors } from '@features/category';
import { MediasPublicModels } from '@features/products/medias';
import { MocacoAttributesPublicConstants, MocacoAttributesPublicFunctions, MocacoAttributesPublicModels, MocacoAttributesPublicSelectors } from '@features/products/mocaco-attributes';
import { ProductPublicModels } from '@features/products/product';
import * as ABTestingHelpers from '@shared/ab-testing/AB-testing.helpers';
import { ABTestingService } from '@shared/ab-testing/services/AB-testing.service';
import { MenuCategory } from '@shared/header/models/category.model';

@Injectable()
export class ProductMocacoService {
    private readonly cmsConfigVisor3d$: Observable<boolean> = this.store.select(getCmsConfigValue('visor_3d'));

    // eslint-disable-next-line no-secrets/no-secrets
    private readonly cmsConfigCustomizableWhiteList$: Observable<string[]> = this.store.select(getCmsConfigValue('customizable_product_whitelist'))
        .pipe(map((value) => Array.isArray(value) ? value : []));

    private readonly categoriesNavigation$ = this.store.select(CategoriesSelectors.getCategoriesNavigation).pipe(
        filter((categoriesNavigation) => categoriesNavigation.length > 0),
        first(),
    );

    constructor(
        private readonly abTestingService: ABTestingService,
        private readonly store: Store,
    ) { }

    public getProductMocacoAttributes(
        product: ProductPublicModels.PublicProduct,
        categoryId: string,
        location?: MediasPublicModels.PublicMediaLocation
    ): Observable<MocacoAttributesPublicModels.PublicMocacoAttribute[]> {
        const mocacoCode: string = MocacoAttributesPublicFunctions.ParseMocacoCode(product);

        return this.store.select(MocacoAttributesPublicSelectors.getAttributesByMocaco(mocacoCode)).pipe(
            withLatestFrom(
                this.cmsConfigCustomizableWhiteList$,
                this.cmsConfigVisor3d$,
                this.categoriesNavigation$.pipe(
                    map((categories) => {
                        return categories.find((category) => category.id === categoryId);
                    })
                ),
            ),
            map(([mocacoAttrs, customizableWhiteList, visor3d, category]) => {
                return this.mapMocacoAttributes(product, location, mocacoAttrs, customizableWhiteList, visor3d, category);
            }),
        );
    }

    public getProductMocacoAttributesSync(
        product: ProductPublicModels.PublicProduct,
        categoryId: string,
        location?: MediasPublicModels.PublicMediaLocation
    ): MocacoAttributesPublicModels.PublicMocacoAttribute[] {
        const mocacoCode: string = MocacoAttributesPublicFunctions.ParseMocacoCode(product);

        const mocacoAttrs = this.store.selectSignal(MocacoAttributesPublicSelectors.getAttributesByMocaco(mocacoCode))();
        // eslint-disable-next-line no-secrets/no-secrets
        const customizableProductWhitelist = this.store.selectSignal(getCmsConfigValue('customizable_product_whitelist'))();
        const visor3D = this.store.selectSignal(getCmsConfigValue('visor_3d'))();
        const categoriesNavigation = this.store.selectSignal(CategoriesSelectors.getCategoriesNavigation)();

        return this.mapMocacoAttributes(
            product,
            location,
            mocacoAttrs,
            Array.isArray(customizableProductWhitelist) ? customizableProductWhitelist : [],
            visor3D,
            categoriesNavigation.find(category => category.id === categoryId),
        );
    }

    private mapMocacoAttributes(
        product: ProductPublicModels.PublicProduct,
        location: MediasPublicModels.PublicMediaLocation,
        mocacoAttrs: MocacoAttributesPublicModels.PublicMocacoAttribute[],
        customizableWhiteList: string[],
        visor3d: boolean,
        category: MenuCategory
    ): MocacoAttributesPublicModels.PublicMocacoAttribute[] {
        const isCategoryPage: boolean = location === 'category_page';

        const mocacoAttributes = MocacoAttributesPublicFunctions.getMocacoAttributes(
            product,
            isCategoryPage,
            mocacoAttrs,
            customizableWhiteList,
            visor3d,
            category
        );

        // ECOMDUTI-14561 TESTAB TEST A/B - Etiquetas artículos
        return ABTestingHelpers.filterMocacoAttrByABTest(
            this.abTestingService,
            mocacoAttributes,
            isCategoryPage
        );
    }

    public hasMocacoAttribute3DVisor(
        product: ProductPublicModels.PublicProduct,
        categoryId: string,
        location?: MediasPublicModels.PublicMediaLocation
    ): Observable<boolean> {
        return this.getProductMocacoAttributes(
            product,
            categoryId,
            location,
        ).pipe(
            map((mocacoAttrs) => mocacoAttrs?.some(
                attribute => attribute.name === MocacoAttributesPublicConstants.MocacoAttributesEnum.VICOM_3D_VISOR
            ))
        );
    }

    public hasMocacoAttributeCustomizable(
        product: ProductPublicModels.PublicProduct,
        categoryId: string,
        location?: MediasPublicModels.PublicMediaLocation
    ): Observable<boolean> {
        return this.getProductMocacoAttributes(
            product,
            categoryId,
            location,
        ).pipe(
            map((mocacoAttrs) => {
                return !!mocacoAttrs && mocacoAttrs?.some(attribute =>
                    attribute.name === MocacoAttributesPublicConstants.MocacoAttributesEnum.XCUSTOMIZABLE);
            }),
            defaultIfEmpty(false)
        );
    }
}
